import React, { useState, useEffect } from 'react';
import { 
    Container, 
    Typography, 
    Box, 
    Paper, 
    Button, 
    Grid,
    IconButton,
    Tooltip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import supabase from '../../supabaseClient';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';

// Ikony dla przełącznika
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import GridViewIcon from '@mui/icons-material/GridView';

// Swiper Component & Styles
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCoverflow, Pagination, Navigation } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

const DeviceIcon = ({ machineId, size = '140px' }) => {
    const [imgError, setImgError] = useState(false);
    const [imageUrl, setImageUrl] = useState('');

    useEffect(() => {
        const { data } = supabase.storage
            .from('devices')
            .getPublicUrl(`${machineId}.png`);

        if (data?.publicUrl) {
            setImageUrl(`${data.publicUrl}?t=${new Date().getTime()}`);
        }
    }, [machineId]);

    if (imgError || !imageUrl) {
        return <PrecisionManufacturingIcon style={{ fontSize: size }} />;
    }

    return (
        <img 
            src={imageUrl} 
            alt="Ikona urządzenia" 
            onError={() => setImgError(true)} 
            style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
        />
    );
};

const WorkerDeviceSelection = () => {
    const [items, setItems] = useState([]);
    const [idConfig, setIdConfiguration] = useState('');
    
    // Odczyt domyślnego trybu z pamięci przeglądarki
    const [viewMode, setViewMode] = useState(() => {
        return localStorage.getItem('preferredDeviceView') || 'carousel';
    });

    const navigate = useNavigate();

    // Przełączanie widoku po kliknięciu w ikonę
    const toggleViewMode = () => {
        const newMode = viewMode === 'carousel' ? 'grid' : 'carousel';
        setViewMode(newMode);
        localStorage.setItem('preferredDeviceView', newMode);
    };

    useEffect(() => {
        const checkSession = async () => {
            const { data } = await supabase.auth.getSession();
            if (data?.session) {
                fetchData(data.session.user.id);
            }
        };
        checkSession();
    }, []);

    const fetchData = async (userId) => {
        const { data: profileData } = await supabase
            .from('profiles')
            .select('id_configuration')
            .eq('id', userId)
            .single();
        if (profileData) {
            setIdConfiguration(profileData.id_configuration);
        }
    };

    useEffect(() => {
        if (idConfig) {
            fetchItems(idConfig);
        }
    }, [idConfig]);

    const fetchItems = async (idConfig) => {
        const { data } = await supabase
            .from('devices')
            .select()
            .eq('id_configuration', idConfig)
            .is('archived', null); 
        if (data) setItems(data);
    };

    const handleMachineSelect = (machineId) => {
        navigate(`/WorkerDevice/${machineId}`);
    };

    return (
        <Container 
            maxWidth="xl" 
            style={{ 
                marginTop: '20px', 
                paddingBottom: '20px', 
                minHeight: '80vh', 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}
        >
            <style>
                {`
                    @keyframes pulse {
                        0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(168, 67, 85, 0.4); }
                        70% { transform: scale(1.02); box-shadow: 0 0 0 15px rgba(168, 67, 85, 0); }
                        100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(168, 67, 85, 0); }
                    }
                    .pulsing-button {
                        animation: pulse 2.5s infinite ease-in-out;
                    }
                `}
            </style>

            {/* ZAWARTOŚĆ GŁÓWNA - WYŚRODKOWANA W POZIOMIE */}
            <Box style={{ width: '100%', flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {items.length > 0 ? (
                    viewMode === 'carousel' ? (
                        /* WIDOK 1: KARUZELA COVERFLOW 3D */
                        <Swiper
                            effect={'coverflow'}
                            grabCursor={true}
                            centeredSlides={true}
                            slidesPerView={'auto'}
                            coverflowEffect={{
                                rotate: 35,
                                stretch: 0,
                                depth: 250,
                                modifier: 1,
                                slideShadows: true,
                            }}
                            pagination={{ clickable: true }}
                            navigation={true}
                            modules={[EffectCoverflow, Pagination, Navigation]}
                            style={{ padding: '20px 0 50px 0', width: '100%' }}
                        >
                            {items.map((machine) => (
                                <SwiperSlide key={machine.id} style={{ width: '320px', height: '420px' }}>
                                    <Paper 
                                        elevation={12} 
                                        style={{ 
                                            borderRadius: '24px', 
                                            height: '100%',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        <Button
                                            variant="contained"
                                            onClick={() => handleMachineSelect(machine.id)}
                                            style={{
                                                height: '100%',
                                                width: '100%',
                                                borderRadius: '24px',
                                                flexDirection: 'column',
                                                justifyContent: 'space-around',
                                                padding: '30px 20px',
                                                textTransform: 'none'
                                            }}
                                            sx={{
                                                backgroundColor: 'primary.main',
                                                '&:hover': {
                                                    backgroundColor: 'primary.dark',
                                                }
                                            }}
                                        >
                                            <Box style={{ width: '140px', height: '140px' }}>
                                                <DeviceIcon machineId={machine.id} size="140px" />
                                            </Box>
                                            <Typography style={{ fontWeight: 'bold', fontSize: '1.8rem', textAlign: 'center' }}>
                                                {machine.name}
                                            </Typography>
                                        </Button>
                                    </Paper>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    ) : (
                        /* WIDOK 2: STANDARDOWA ELASTYCZNA SIATKA (GRID) */
                        <Grid container spacing={3} justifyContent="center" style={{ marginTop: '10px', width: '100%' }}>
                            {items.map((machine) => (
                                <Grid key={machine.id} item xs={12} sm={6} md={4} lg={3}>
                                    <Paper 
                                        elevation={6} 
                                        style={{ borderRadius: '20px', overflow: 'hidden', height: '220px' }}
                                    >
                                        <Button
                                            className="pulsing-button"
                                            variant="contained"
                                            fullWidth
                                            onClick={() => handleMachineSelect(machine.id)}
                                            style={{
                                                height: '100%',
                                                borderRadius: '20px',
                                                textTransform: 'none',
                                                padding: '20px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                textAlign: 'center'
                                            }}
                                            sx={{
                                                backgroundColor: 'primary.light',
                                                '&:hover': { backgroundColor: 'primary.main' }
                                            }}
                                        >
                                            <Box style={{ width: '80px', height: '80px', marginBottom: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                <DeviceIcon machineId={machine.id} size="80px" />
                                            </Box>
                                            <Typography 
                                                style={{ 
                                                    fontWeight: 'bold', 
                                                    fontSize: '1.3rem', 
                                                    lineHeight: '1.2',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden'
                                                }}
                                            >
                                                {machine.name}
                                            </Typography>
                                        </Button>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    )
                ) : (
                    <Box textAlign="center" marginTop={8}>
                        <Typography variant="h5" color="textSecondary">
                            Brak dostępnych maszyn dla tego profilu.
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* JEDNA IKONKA-PRZEŁĄCZNIK IDEALNIE NA ŚRODKU NA DOLE STRONY */}
            <Box style={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                <Tooltip title={viewMode === 'carousel' ? 'Przełącz na Kafelki' : 'Przełącz na Karuzelę'}>
                    <IconButton 
                        onClick={toggleViewMode} 
                        color="primary"
                        sx={{ 
                            padding: '12px',
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                            '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.2)',
                            }
                        }}
                    >
                        {viewMode === 'carousel' ? (
                            <ViewCarouselIcon style={{ fontSize: '36px' }} />
                        ) : (
                            <GridViewIcon style={{ fontSize: '36px' }} />
                        )}
                    </IconButton>
                </Tooltip>
            </Box>
        </Container>
    );
};

export default WorkerDeviceSelection;